<template>
  <div class="mt-3">
    <b-row class="arrivals-table-header" align-v="center">
      <b-col class="arrivals-header-name" sm="6">
        <b-input-group v-if="search">
          <b-form-input v-model="filter" :placeholder="$t('search')" />
        </b-input-group>
      </b-col>
      <b-col class="text-right" sm="6">
        <span
          @click="downloadReport"
          style="cursor: pointer"
          title="Download Report"
        >
          <i class="fi fi-download" />
        </span>
      </b-col>
    </b-row>

    <b-table
      :class="['analytics-tables table-has-border', tableClass]"
      responsive
      show-empty
      striped
      :stacked="stacked"
      :filter="filter"
      :items="items"
      :fields="fields"
      :empty-text="'No Data Available For Selected Date Range'"
    >
      <slot name="custom-slots" />
      <template v-slot:empty="scope">
        <h6>{{ scope.emptyText }}</h6>
      </template>
    </b-table>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { downloadBase64 } from "@/utils/download";
import AnalyticsMixin from "./mixin";
import axios from "axios";

export default {
  name: "report-base",

  props: {
    items: {
      type: Array,
      default: () => [],
    },

    fields: {
      type: Array,
      default: () => [],
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    search: {
      type: Boolean,
      default: true,
    },

    stacked: {
      type: Boolean,
      default: false,
    },

    subject: {
      type: String,
      default: "",
    },

    tableClass: {
      type: String,
      default: "",
    },
  },

  mixins: [AnalyticsMixin],

  components: {
    SpinnerLoader: () => import("@/components/widgets/SpinnerLoader"),
  },

  data() {
    return {
      filter: "",
      isDownloading: false,
    };
  },

  computed: {
    ...mapGetters(["getVendor"]),
  },

  watch: {
    getDateRange: function () {
      this.$emit("reload");
    },
  },

  methods: {
    async downloadReport() {
      if (this.isDownloading) return;
      this.isDownloading = true;

      const params = `vendor_id=${this.getVendor.id}&starts_at=${this.startDate}&ends_at=${this.endDate}`;
      const url = `${process.env.ROOT_API}/api/analytics/export/${this.subject}?${params}`;

      try {
        const httpClient = this.$axios || axios;

        const authToken = localStorage.getItem("access_token");

        if (!authToken) {
          console.error(
            "Authentication token not found in localStorage. Cannot download report."
          );
          alert("Authentication error. Please try logging out and back in.");
          this.isDownloading = false;
          return;
        }

        const response = await httpClient.get(url, {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        let fileName = `${this.subject}_report.csv`;
        const contentDisposition = response.headers["content-disposition"];
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (fileNameMatch && fileNameMatch.length === 2) {
            fileName = fileNameMatch[1];
          }
        }
        link.setAttribute("download", fileName);

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error("Error downloading report. URL:", url);
        if (error.response) {
          console.error("Response Data:", error.response.data);
          try {
            const errorText = await error.response.data.text();
            console.error("Response Text:", errorText);
          } catch (e) {
            console.error("Could not read error response data as text.");
          }
          console.error("Response Status:", error.response.status);
          console.error("Response Headers:", error.response.headers);
        } else if (error.request) {
          console.error("Request Data:", error.request);
        } else {
          console.error("Error Message:", error.message);
        }
        console.error("Error Config:", error.config);
        alert(
          "Failed to download report. Please check browser console for details (Right-click -> Inspect -> Console)."
        );
      } finally {
        this.isDownloading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.arrivals-table-header {
  width: 100%;
  margin: 0px;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: justify;
  -ms-flex-pack: justify;
  justify-content: space-between;

  .fi-print {
    color: #2c3e50;
    cursor: pointer;
    font-size: 24px;
  }
}

.arrivals-header-name {
  padding-left: 10px;
  text-align: left;
}

.analytics-tables {
  overflow: auto;
  position: absolute;
  top: 120px;
  bottom: 0;
  left: 0;
  right: 0;

  table {
    min-width: 1500px;
    position: relative;
  }

  th {
    background-color: #f5f5f5;
    position: sticky !important;
    top: -1px;
  }

  th,
  td {
    font-size: 14px !important;
  }
}
</style>
